/**
 * SandboxLogsOverlay — slide-up logs panel over the sandbox canvas, mirroring
 * the Flow Designer settings overlay (WorkflowSettingsOverlay): a background
 * that slides up from the bottom, a close button where the trigger sat, and
 * the content fading in on top. Render under <AnimatePresence> in the canvas
 * so the exit animation plays.
 *
 * Content is the existing full-height SandboxLogsTab viewer.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Button, Text } from '@chatsdet/ui';
import { Terminal, X } from '@phosphor-icons/react';
import { SandboxLogsTab } from './SandboxLogsTab';

const EASE = [0.32, 0.72, 0, 1] as const;

export interface SandboxLogsOverlayProps {
  isOpen: boolean;
  instanceId: string;
  onClose: () => void;
}

export function SandboxLogsOverlay({ isOpen, instanceId, onClose }: SandboxLogsOverlayProps) {
  React.useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Background — slides up; on exit waits for the content fade then drops. */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0, transition: { duration: 0.3, ease: EASE } }}
        exit={{ y: '100%', transition: { duration: 0.25, delay: 0.2, ease: EASE } }}
        className="absolute inset-0 z-40 bg-background"
      />

      {/* Close button — top-right, clear of the title. */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1, transition: { duration: 0.2, delay: 0.25 } }}
        exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
        className="absolute top-0 right-2 z-50 h-14 flex items-center"
      >
        <Button variant="icon" iconOnly tooltip="Close logs" onPress={onClose}>
          <X size={20} />
        </Button>
      </motion.div>

      {/* Content — fades in after the background lands. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.25, delay: 0.2 } }}
        exit={{ opacity: 0, transition: { duration: 0.15 } }}
        className="absolute inset-0 z-40 flex flex-col"
      >
        <div className="flex items-center gap-2 h-14 shrink-0 px-4 border-b border-border">
          <Terminal size={16} weight="fill" className="text-muted-foreground" />
          <Text weight="semibold" className="text-base">
            Logs
          </Text>
        </div>
        <div className="flex-1 min-h-0">
          <SandboxLogsTab instanceId={instanceId} />
        </div>
      </motion.div>
    </>
  );
}

SandboxLogsOverlay.displayName = 'SandboxLogsOverlay';
